// One writer per browser. Every DM Screen tab receives the same bridge events
// (Roll20 bars, sheet polls), so without this two open tabs would both write
// the same change to D&D Beyond and Roll20, and their interleaved writes can
// disagree. Tabs race for a localStorage lease; the holder renews it every
// 2 s and the others take over if it goes stale (tab closed, computer asleep).
import { useEffect, useState } from "react";

const KEY = "dm-screen-writer-lease";
const TTL_MS = 6000;
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function readLease(): { id: string; at: number } | null {
  try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function claim(): boolean {
  const now = Date.now();
  const lease = readLease();
  if (lease && lease.id !== TAB_ID && now - lease.at < TTL_MS) return false;
  try { localStorage.setItem(KEY, JSON.stringify({ id: TAB_ID, at: now })); } catch { return true; /* no storage: the only tab that matters */ }
  return readLease()?.id === TAB_ID;
}
function release() {
  try { if (readLease()?.id === TAB_ID) localStorage.removeItem(KEY); } catch { /* nothing to release */ }
}

/** True in exactly one DM Screen tab of this browser at a time. */
export function useWriterLease(): boolean {
  const [leader, setLeader] = useState(false);
  useEffect(() => {
    const tick = () => setLeader(claim());
    tick();
    const timer = window.setInterval(tick, 2000);
    const onStorage = (e: StorageEvent) => { if (e.key === KEY) tick(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("beforeunload", release);
    return () => { window.clearInterval(timer); window.removeEventListener("storage", onStorage); window.removeEventListener("beforeunload", release); release(); };
  }, []);
  return leader;
}
